import { Container } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { LANDING_MAX_WIDTH } from '../landing-theme';

/**
 * Centered content column — the `.section-shell` rule of the marketing CSS.
 *
 * Built on MUI's `Container` (the same primitive the product uses in
 * `page_layout.tsx`) rather than a hand-rolled width formula, so the shell behaves like the
 * rest of the app. `maxWidth={false}` opts out of `Container`'s width steps so we can pin the
 * cap to the marketing layout's own `--max` (1240px) instead of MUI's nearest breakpoint.
 *
 * Gutters are intentionally wider than the product's default (16/24px at xs/sm from
 * `page_layout.tsx`'s `Container maxWidth="lg"`, since the theme adds no `MuiContainer`
 * override): the landing sets display type up to `clamp(3rem, 5.2vw, 5.2rem)`, and gutters
 * calibrated for a purchase form read as cramped next to type that large.
 */
export function SectionShell(props: {
  children: ReactNode;
  component?: 'section' | 'header' | 'footer' | 'div';
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Container
      component={props.component ?? 'section'}
      aria-label={props['aria-label']}
      maxWidth={false}
      sx={[
        {
          maxWidth: LANDING_MAX_WIDTH,
          mx: 'auto',
          // `sx` is applied after `Container`'s own padding classes, so this wins outright
          // rather than stacking on top of it.
          px: { xs: 3, sm: 4, md: 5 },
        },
        ...(Array.isArray(props.sx) ? props.sx : props.sx ? [props.sx] : []),
      ]}
    >
      {props.children}
    </Container>
  );
}
